import React from 'react';
import './MarketAnalysis.css';

function MarketAnalysis({ analysis, keywords, region }) {
  const [exporting, setExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState(null);

  // 添加详细的数据结构日志
  console.log('MarketAnalysis received raw:', analysis);

  // Handle export functionality
  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    
    try {
      const response = await fetch('/api/export/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysis,
          keywords,
          region,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Create a hidden anchor tag to trigger download
        const link = document.createElement('a');
        link.href = data.download_url;
        link.download = 'market_analysis_report.docx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(data.error || 'Export failed');
      }
    } catch (error) {
      setExportError(error.message);
    } finally {
      setExporting(false);
    }
  };

  // Handle error state
  if (analysis.status === 'error') {
    return (
      <div className="alert alert-warning">
        <h4>Analysis Generation Error</h4>
        <p>{analysis.error || 'Failed to generate market analysis'}</p>
        <p className="text-muted">Generated on: {new Date(analysis.timestamp).toLocaleDateString()}</p>
      </div>
    );
  }

  // Handle empty or invalid analysis
  if (!analysis || typeof analysis !== 'object' || Object.keys(analysis).length === 0) {
    console.warn('Analysis object is empty');
    return (
      <div className="alert alert-info">
        <h4>No Analysis Available</h4>
        <p>Market analysis is not available for the current search.</p>
      </div>
    );
  }

  // Get content
  const content = analysis.content || analysis.analysis || analysis.message?.content;
  
  if (!content) {
    return (
      <div className="alert alert-warning">
        <h4>No Analysis Content</h4>
        <p>The analysis was generated but contains no content.</p>
        <p className="text-muted">Generated on: {new Date(analysis.timestamp).toLocaleDateString()}</p>
      </div>
    );
  }

  // 处理 Markdown 格式
  const formatContent = (content) => {
    console.log('Original content:', content);

    // 将连续的换行符替换为单个换行符
    let formatted = content.replace(/\n{3,}/g, '\n\n');
    console.log('After newline formatting:', formatted);
    
    // 移除第一个标题（包括其后的换行符）
    formatted = formatted.replace(/^# [^\n]+\n+/, '');
    console.log('After removing first title:', formatted);
    
    // 处理标题格式（先处理较长的标题格式，避免匹配冲突）
    formatted = formatted.replace(/^(?:\d+\.\s+)?### (.*$)/gm, '<h4 class="section-subtitle">$1</h4>');
    formatted = formatted.replace(/^(?:\d+\.\s+)?## (.*$)/gm, '<h3 class="section-title">$1</h3>');
    formatted = formatted.replace(/^(?:\d+\.\s+)?# (.*$)/gm, '<h2 class="section-heading">$1</h2>');
    
    // 处理带数字编号的标题（没有 #）
    formatted = formatted.replace(/^(\d+)\.\s+(Executive Summary|Recent Industry News & Developments|Social Media Sentiment Analysis|Opportunities & Risks Assessment|Future Outlook)(.*$)/gm, 
      '<h2 class="section-heading">$2$3</h2>');
    
    console.log('After heading formatting:', formatted);
    
    // 将文本分成段落，但保持原有的换行
    let paragraphs = formatted.split('\n\n').map(p => p.trim()).filter(Boolean);
    console.log('Split into paragraphs:', paragraphs);
    
    // 处理每个段落
    formatted = paragraphs.map(paragraph => {
      // 跳过空段落和分隔符
      if (!paragraph.trim() || paragraph.trim() === '---') {
        return '';
      }
      
      console.log('Processing paragraph:', paragraph);
      
      // 如果段落已经是 HTML
      if (paragraph.trim().startsWith('<') && paragraph.trim().endsWith('>')) {
        console.log('Keeping HTML as is:', paragraph);
        return paragraph.trim();
      }
      
      // 如果段落包含列表项
      if (/^[*-] /m.test(paragraph)) {
        console.log('Found list in paragraph');
        // 处理列表项
        const listItems = paragraph
          .split('\n')
          .filter(line => line.trim() && line.trim() !== '---')
          .map(line => {
            if (line.trim().startsWith('* ') || line.trim().startsWith('- ')) {
              return `<li>${line.trim().substring(2)}</li>`;
            }
            return line;
          })
          .join('\n');
        return `<ul>\n${listItems}\n</ul>`;
      }
      
      // 处理包含多行的段落
      const lines = paragraph.split('\n')
        .map(line => line.trim())
        .filter(line => line && line !== '---');
      if (lines.length > 1) {
        return `<p>${lines.join('<br>')}</p>`;
      }
      
      // 普通段落
      console.log('Wrapping as paragraph:', paragraph);
      return `<p>${paragraph.trim()}</p>`;
    }).filter(Boolean).join('\n');
    
    console.log('Final formatted content:', formatted);
    return formatted;
  };

  // 从内容中提取主标题
  const getMainTitle = (content) => {
    const match = content.match(/^# ([^\n]+)/);
    return match ? match[1] : 'Market Analysis Report';
  };

  return (
    <div className="market-analysis">
      <div className="analysis-header">
        <div className="header-content">
          <h2 className="report-title">Market Analysis Report</h2>
          <p className="analysis-timestamp">
            Generated on: {new Date(analysis.timestamp).toLocaleDateString()}
          </p>
        </div>
        <div className="export-controls">
          <button 
            className="export-button" 
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export to Word'}
          </button>
          {exportError && (
            <p className="export-error">Export failed: {exportError}</p>
          )}
        </div>
      </div>
      <div 
        className="analysis-content"
        dangerouslySetInnerHTML={{ __html: formatContent(content) }}
      />
    </div>
  );
}

export default MarketAnalysis;
